/**
 * Live dashboard home — lists available public events to view.
 *
 * Requirement: live dashboard root offers a directory of public events.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePublicApiClient } from '../hooks/usePublicApiClient.js';
import { EventStatusBadge } from '../components/EventStatusBadge.js';
import { LeaderboardHeader } from '../components/LeaderboardHeader.js';
import { DonationButton } from '@scoring/frontend-shared';

interface PublicEvent {
  id: string;
  name: string;
  date: string;
  location: string;
  status: string;
}

export function HomePage(): JSX.Element {
  const apiClient = usePublicApiClient();
  const [events, setEvents] = useState<PublicEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .request<{ events: PublicEvent[] }>('GET', '/public/events', { public: true })
      .then((data) => {
        if (!cancelled) setEvents(data.events);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load events');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiClient]);

  // Active events first, then the rest.
  const sorted = [...events].sort((a, b) => {
    if (a.status === b.status) return a.name.localeCompare(b.name);
    return a.status === 'active' ? -1 : b.status === 'active' ? 1 : 0;
  });

  return (
    <div className="leaderboard-container">
      <LeaderboardHeader />
      <h1 className="event-title">Live Leaderboards</h1>
      <p className="home-subtitle">Select an event to view its live standings.</p>

      {loading && <p className="loading-message">Loading events…</p>}
      {error && <p className="error-message" role="alert">{error}</p>}
      {!loading && !error && sorted.length === 0 && (
        <p className="empty-message">No public events available right now.</p>
      )}

      <ul className="event-list">
        {sorted.map((event) => (
          <li key={event.id}>
            <Link to={`/${event.id}`} className="event-card">
              <div className="event-card-main">
                <span className="event-card-name">{event.name}</span>
                <span className="event-card-meta">
                  {new Date(event.date).toLocaleDateString()} · {event.location}
                </span>
              </div>
              <EventStatusBadge status={event.status} />
            </Link>
          </li>
        ))}
      </ul>

      <footer className="leaderboard-footer">
        <DonationButton />
      </footer>
    </div>
  );
}
